import type { RealtimeDocStatus, DocumentStatusState } from "../hooks/useDocumentProcessingStream";
import {
  isEtlComplete,
  isEtlInReview,
  isExtractionStuckInQueue,
  isExtractionUiComplete,
  isTerminalEtlStatus,
  normalizeEtlStatus,
} from "./documentExtractionStatus";

export type DocumentUploadContextType = "asset" | "location" | "library";

export interface DocumentUploadContext {
  type: DocumentUploadContextType;
  id?: string;
  label?: string;
}

export interface DocumentExtractionJob {
  documentId: string;
  name: string;
  mime_type?: string;
  uploadedAt: string;
  status: RealtimeDocStatus;
  progress: number | null;
  stage: string | null;
  error: string | null;
  updatedAt: string;
  contextType: DocumentUploadContextType;
  contextId?: string;
  contextLabel?: string;
  read: boolean;
}

export interface StatusEventPayload {
  documentId: string;
  status: RealtimeDocStatus;
  error?: string | null;
  processed_at?: string | null;
  progress?: number | null;
  stage?: string | null;
}

export const EXTRACTION_JOBS_STORAGE_KEY = "documentExtractionJobs:v1";
export const EXTRACTION_DEBUG_SSE_KEY = "DEBUG_DOC_EXTRACTION_SSE";
export const PRUNE_JOBS_AFTER_DAYS = 7;
export const MAX_STORED_JOBS = 50;

export function isDebugSseEnabled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return (
      process.env.NODE_ENV === "development" ||
      localStorage.getItem(EXTRACTION_DEBUG_SSE_KEY) === "1"
    );
  } catch {
    return false;
  }
}

export function logSseDebug(message: string, data?: unknown): void {
  if (!isDebugSseEnabled()) return;
  console.debug(`[DocumentExtraction SSE] ${message}`, data ?? "");
}

export function normalizeStreamStatus(raw: string): RealtimeDocStatus {
  if (raw === "queued") return "pending";
  if (raw === "fail") return "failed";
  if (raw === "completed") return "in_review";
  if (raw === "pending_user_review") return "in_review";
  return raw as RealtimeDocStatus;
}

/**
 * Stream must not promote jobs to user-complete during extraction.
 * Only markJobSubmitted / API bootstrap with confirmed complete should set complete.
 */
export function coerceStreamJobStatus(
  job: DocumentExtractionJob,
  status: RealtimeDocStatus,
  fromBootstrap = false
): RealtimeDocStatus {
  if (fromBootstrap || status !== "complete") return status;
  if (normalizeEtlStatus(job.status) === "complete") return status;
  return "in_review";
}

/** Pure merge for SSE/bootstrap updates — unit-testable without network. */
export function applyStatusEvent(
  job: DocumentExtractionJob,
  payload: StatusEventPayload,
  options?: { fromBootstrap?: boolean }
): DocumentExtractionJob {
  const normalized = normalizeStreamStatus(String(payload.status));
  const status = coerceStreamJobStatus(job, normalized, options?.fromBootstrap);
  const incomingProgress =
    payload.progress != null && !Number.isNaN(Number(payload.progress))
      ? Math.max(0, Math.min(100, Math.round(Number(payload.progress))))
      : undefined;

  const progress =
    incomingProgress !== undefined ? incomingProgress : job.progress;

  const stage =
    payload.stage != null && payload.stage !== ""
      ? payload.stage
      : job.stage;

  const updatedAt = payload.processed_at ?? new Date().toISOString();

  const terminal = isTerminalEtlStatus(status);
  const read =
    terminal &&
    (status === "complete" || status === "in_review")
      ? false
      : job.read;

  return {
    ...job,
    status,
    progress,
    stage,
    error: payload.error ?? job.error,
    updatedAt,
    read: status === "failed" ? false : read,
  };
}

export function shouldApplyBootstrap(
  job: DocumentExtractionJob,
  bootstrapFetchedAt: string,
  backendProgress?: number | null,
  backendStatus?: string | null
): boolean {
  const backendN = normalizeEtlStatus(backendStatus);
  const jobN = normalizeEtlStatus(job.status);
  if (backendN === "in_review" && isEtlComplete(jobN)) return true;
  if (backendN && jobN && backendN !== jobN) return true;
  if (backendProgress != null && !Number.isNaN(Number(backendProgress))) {
    const backend = Math.max(0, Math.min(100, Math.round(Number(backendProgress))));
    if ((job.progress ?? 0) < backend) return true;
  }
  if (!job.updatedAt) return true;
  return new Date(bootstrapFetchedAt).getTime() >= new Date(job.updatedAt).getTime();
}

/** Keep the higher progress when merging job state from API/register. */
export function resolveJobProgress(
  existing: number | null | undefined,
  incoming: number | null | undefined
): number | null {
  if (incoming != null && !Number.isNaN(Number(incoming))) {
    const inc = Math.max(0, Math.min(100, Math.round(Number(incoming))));
    if (existing != null && !Number.isNaN(Number(existing))) {
      return Math.max(Math.round(Number(existing)), inc);
    }
    return inc;
  }
  return existing ?? null;
}

/** Max of live statusMap progress and document etl progress. */
export function resolveExtractionProgress(
  liveProgress: number | null | undefined,
  docEtlProgress: number | null | undefined
): number | null {
  return resolveJobProgress(liveProgress, docEtlProgress);
}

export function jobToStatusState(job: DocumentExtractionJob): DocumentStatusState {
  return {
    status: job.status,
    updatedAt: job.updatedAt,
    error: job.error,
    progress: job.progress,
    stage: job.stage,
  };
}

export function jobsToStatusMap(jobs: Record<string, DocumentExtractionJob>): Record<string, DocumentStatusState> {
  const map: Record<string, DocumentStatusState> = {};
  for (const id of Object.keys(jobs)) {
    map[id] = jobToStatusState(jobs[id]);
  }
  return map;
}

export function isJobActive(job: DocumentExtractionJob): boolean {
  const s = job.status;
  return s === "pending" || s === "processing";
}

/** Queued at 0% longer than STUCK_QUEUE_THRESHOLD_MS — treat like failed in UI. */
export function isJobStuckInQueue(job: DocumentExtractionJob): boolean {
  return isExtractionStuckInQueue(job.status, job.progress, job.uploadedAt);
}

/** Still extracting — not yet ready for user review. */
export function isJobInProgress(job: DocumentExtractionJob): boolean {
  return isJobActive(job) && !isJobInReview(job) && !isJobStuckInQueue(job);
}

export function isJobFailed(job: DocumentExtractionJob): boolean {
  return job.status === "failed" || isJobStuckInQueue(job);
}

/** AI extraction finished — user must review and submit. */
export function isJobInReview(job: DocumentExtractionJob): boolean {
  if (isJobFailed(job) || isEtlComplete(job.status)) return false;
  if (isEtlInReview(job.status)) return true;
  return (
    isJobActive(job) &&
    isExtractionUiComplete(job.status, job.progress) &&
    !isEtlComplete(job.status)
  );
}

/** User submitted reviewed data — fully completed. */
export function isJobCompleted(job: DocumentExtractionJob): boolean {
  return isEtlComplete(job.status);
}

export function categorizeExtractionJobs(jobs: Record<string, DocumentExtractionJob>): {
  inProgress: DocumentExtractionJob[];
  inReview: DocumentExtractionJob[];
  completed: DocumentExtractionJob[];
  failed: DocumentExtractionJob[];
} {
  const list = Object.values(jobs);
  const inProgress = list
    .filter(isJobInProgress)
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  const inReview = list
    .filter(isJobInReview)
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  const completed = list
    .filter(isJobCompleted)
    .sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime());
  const failed = list
    .filter(isJobFailed)
    .sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime());
  return { inProgress, inReview, completed, failed };
}

export function countActiveJobs(jobs: Record<string, DocumentExtractionJob>): number {
  return Object.values(jobs).filter(isJobInProgress).length;
}

export function countUnreadJobs(jobs: Record<string, DocumentExtractionJob>): number {
  return Object.values(jobs).filter(
    (j) =>
      !j.read &&
      (isJobInReview(j) || isJobCompleted(j) || isJobFailed(j))
  ).length;
}

export function countBadgeJobs(jobs: Record<string, DocumentExtractionJob>): number {
  const { inProgress, inReview, completed, failed } = categorizeExtractionJobs(jobs);
  const unreadDone = [...inReview, ...completed, ...failed].filter((j) => !j.read).length;
  return inProgress.length + unreadDone;
}

export function pruneJobs(jobs: Record<string, DocumentExtractionJob>): Record<string, DocumentExtractionJob> {
  const cutoff = Date.now() - PRUNE_JOBS_AFTER_DAYS * 24 * 60 * 60 * 1000;
  const entries = Object.values(jobs)
    .filter((j) => new Date(j.uploadedAt).getTime() >= cutoff)
    .sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime())
    .slice(0, MAX_STORED_JOBS);

  const next: Record<string, DocumentExtractionJob> = {};
  for (const job of entries) {
    next[job.documentId] = job;
  }
  return next;
}

/** Remove jobs for hard-deleted documents from the notification store. */
export function removeJobsByDocumentIds(
  jobs: Record<string, DocumentExtractionJob>,
  documentIds: string | string[]
): Record<string, DocumentExtractionJob> {
  const ids = Array.isArray(documentIds) ? documentIds : [documentIds];
  if (ids.length === 0) return jobs;
  const idSet = new Set(ids.filter(Boolean));
  if (idSet.size === 0) return jobs;

  let changed = false;
  const next: Record<string, DocumentExtractionJob> = {};
  for (const [id, job] of Object.entries(jobs)) {
    if (idSet.has(id)) {
      changed = true;
      continue;
    }
    next[id] = job;
  }
  return changed ? next : jobs;
}

export function loadJobsFromStorage(): Record<string, DocumentExtractionJob> {
  if (typeof window === "undefined") return {};
  try {
    const raw = localStorage.getItem(EXTRACTION_JOBS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as { jobs?: DocumentExtractionJob[] };
    if (!Array.isArray(parsed.jobs)) return {};
    const map: Record<string, DocumentExtractionJob> = {};
    for (const job of parsed.jobs) {
      if (!job?.documentId) continue;
      const status =
        job.status === "completed"
          ? ("in_review" as RealtimeDocStatus)
          : job.status;
      map[job.documentId] = { ...job, status };
    }
    return pruneJobs(map);
  } catch {
    return {};
  }
}

export function saveJobsToStorage(jobs: Record<string, DocumentExtractionJob>): void {
  if (typeof window === "undefined") return;
  try {
    const pruned = pruneJobs(jobs);
    localStorage.setItem(
      EXTRACTION_JOBS_STORAGE_KEY,
      JSON.stringify({ jobs: Object.values(pruned) })
    );
  } catch {
    // ignore quota errors
  }
}

export function clearJobsStorage(): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(EXTRACTION_JOBS_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/** Whether API/search status should overwrite a stored job (fixes stale complete, etc.). */
export function shouldSyncJobFromApi(
  existing: DocumentExtractionJob,
  apiStatusRaw: string
): boolean {
  const apiN = normalizeEtlStatus(normalizeStreamStatus(apiStatusRaw));
  const existingN = normalizeEtlStatus(existing.status);
  if (!apiN) return false;
  if (isJobActive(existing)) return true;
  if (apiN === "in_review" && isEtlComplete(existingN)) {
    return true;
  }
  if (apiN === "complete" && existingN === "in_review") {
    return true;
  }
  return apiN !== existingN;
}

export function buildDefaultContextLabel(ctx: DocumentUploadContext): string {
  if (ctx.label) return ctx.label;
  switch (ctx.type) {
    case "asset":
      return ctx.id ? `Asset` : "Asset";
    case "location":
      return ctx.id ? `Location` : "Location";
    case "library":
      return "Document library";
    default:
      return "Documents";
  }
}
